import { useDispatch, useSelector } from 'react-redux';
import { InlineLink, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { FamilyPlan } from '@app/views/ChangePlan/types';
import { openInNewTab, selectChosenPlan, selectConfig } from '@app/redux';

import { Platform } from '@mfe/shared/schema-types';

import styles from './styles.module.scss';

const SMBContent = () => {
  const { t } = useTranslation('ChangePlan', {
    keyPrefix: 'orderReview.details.terms.modal',
  });
  const dispatch = useDispatch();

  const { platform } = useSelector(selectConfig);
  const { characteristics } = useSelector(selectChosenPlan);
  const { productFamily, dataCap } = characteristics;
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (platform === Platform.Web) {
      return;
    }

    e.preventDefault();
    dispatch(openInNewTab(t('content.smb.anyToChoice.url')));
  };

  // as per gfbapps-1714, currently business caf II plans and business choice 300gb are to keep using old terms and conditions copy
  const shouldUseNewTsAndCs = !(
    productFamily === FamilyPlan.BusinessCAF ||
    (productFamily === FamilyPlan.BusinessChoice && dataCap === '300')
  );

  return (
    <>
      <ul>
        {Object.values(
          t('content.smb.list', {
            returnObjects: true,
            contractLength: shouldUseNewTsAndCs ? '12-month' : '24-month',
          })
        ).map((text: string | { regular: string; bold: string }, index: number) => {
          if (typeof text === 'object') {
            return (
              <li key={index}>
                <Txt component="p" variant="tinyRegular">
                  <Txt component="span" variant="tinyRegular">
                    {text.regular}
                  </Txt>{' '}
                  <Txt component="span" variant="tinyBold">
                    {text.bold}
                  </Txt>
                </Txt>
              </li>
            );
          }

          return (
            <li key={index}>
              <Txt component="p" variant="tinyRegular">
                {text}
              </Txt>
            </li>
          );
        })}
      </ul>

      <Txt component="p" variant="tinyRegular">
        {t('content.smb.table.intro1')}
      </Txt>
      <Txt component="p" variant="tinyRegular">
        {shouldUseNewTsAndCs ? t('content.smb.table.intro2NewTsAndCs') : t('content.smb.table.intro2')}
      </Txt>
      <table className={styles['table']}>
        <thead>
          <tr>
            {Object.values(t('content.smb.table.columns', { returnObjects: true })).map(
              (text: string, index: number) => (
                <th key={index}>
                  <Txt component="span" variant="tinyBold">
                    {text}
                  </Txt>
                </th>
              )
            )}
          </tr>
        </thead>

        <tbody>
          {Object.values(
            t(shouldUseNewTsAndCs ? 'content.smb.table.rowsNewTsAndCs' : 'content.smb.table.rows', {
              returnObjects: true,
            })
          ).map(({ planName, threshold }: { planName: string; threshold: string }, index: number) => {
            return (
              <tr key={index}>
                <td>
                  <Txt component="span" variant="tinyRegular">
                    {planName}
                  </Txt>
                </td>
                <td>
                  <Txt component="span" variant="tinyRegular">
                    {threshold}
                  </Txt>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Txt component="p" variant="tinyBold">
        {t('content.smb.anyToChoice.title')}
      </Txt>
      <ul>
        <li>
          <Txt component="p" variant="tinyRegular">
            {t('content.smb.anyToChoice.officeHours')}
          </Txt>
        </li>
        <li>
          <Txt component="p" variant="tinyRegular">
            <span>
              {t('content.smb.anyToChoice.explanation')}{' '}
              <InlineLink
                style={{ lineHeight: 'inherit' }}
                onClick={handleClick}
                href={t('content.smb.anyToChoice.url')}
                target="_blank"
              >
                {t('content.smb.anyToChoice.url')}
              </InlineLink>
            </span>
          </Txt>
        </li>
      </ul>

      <Txt component="p" variant="tinyBold">
        {t('content.smb.anyToChoiceBackup.title')}
      </Txt>
      <ul>
        <li>
          <Txt component="p" variant="tinyRegular">
            {t('content.smb.anyToChoiceBackup.explanation')}
          </Txt>
        </li>
      </ul>
    </>
  );
};

export default SMBContent;
